// src/components/Estadistica.tsx
import React, { useState } from 'react';
import { BillType } from '../../types';
import './Estadistica.scss';

// Importamos el componente de tarjetas y el de gráfico
import CardSection from './components/CardSection';
import ChartSection from './components/ChartSection';

type EstadisticaProps = {
  billsList: BillType[];
};

/**
 * Devuelve un array de fechas (Date) desde `start` hasta `end` (inclusive),
 * limpiando horas/minutos/segundos.
 */
function getDaysRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  let current = new Date(start);
  current.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(0, 0, 0, 0);

  while (current <= last) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

/**
 * Devuelve un array de objetos { month, year } desde startMonth/startYear
 * hasta endMonth/endYear (inclusive), avanzando mes a mes.
 */
function getMonthYearRange(
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number
): { month: number; year: number }[] {
  const result: { month: number; year: number }[] = [];
  const current = new Date(startYear, startMonth - 1, 1);
  const last = new Date(endYear, endMonth - 1, 1);

  while (current <= last) {
    result.push({ month: current.getMonth() + 1, year: current.getFullYear() });
    current.setMonth(current.getMonth() + 1);
  }
  return result;
}

const Estadistica: React.FC<EstadisticaProps> = ({ billsList }) => {
  // 1) Estados de filtros de fecha (año y mes de inicio / fin)
  const [startMonth, setStartMonth] = useState(1);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);
  const [endYear, setEndYear] = useState(new Date().getFullYear());

  // Para dropdown de años (últimos 10 años)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  // 2) Determinamos fecha mínima y máxima de todas las facturas
  const allTimestamps = billsList.map((b) => new Date(b.bill_date).getTime());
  const minDate = new Date(Math.min(...allTimestamps));
  const maxDate = new Date(Math.max(...allTimestamps));

  // 3) Construimos objetos Date según selección del usuario (primer día de mes)
  const userStart = new Date(startYear, startMonth - 1, 1);
  const userEnd = new Date(endYear, endMonth - 1, 1);
  // Ajustamos que no excedan el rango real de billsList
  const actualStart = userStart < minDate ? minDate : userStart;
  const actualEnd = userEnd > maxDate ? maxDate : userEnd;

  // 4) Filtramos las facturas que caen entre actualStart y actualEnd (inclusive)
  const filteredBills = billsList.filter((b) => {
    const d = new Date(b.bill_date);
    return d >= actualStart && d <= actualEnd;
  });

  // ----------------------------
  // 5) Construir datos DIARIOS
  // ----------------------------
  // 5.1) Generamos un array de fechas diarias (limpiando HH:MM:SS)
  const daysRange = getDaysRange(actualStart, actualEnd);
  // 5.2) Formateamos a ISO "YYYY-MM-DD"
  const dailyLabels: string[] = daysRange.map((d) => d.toISOString().split('T')[0]);

  // 5.3) Función para agrupar facturas por categoría (misma usada para diario y mensual)
  const groupBy = (
    bills: BillType[],
    key: 'global_bill_category_id' | 'local_bill_category_id'
  ): Record<number, BillType[]> => {
    return bills.reduce((acc: Record<number, BillType[]>, b) => {
      const cid = b[key];
      if (cid != null) {
        if (!acc[cid]) {
          acc[cid] = [];
        }
        acc[cid].push(b);
      }
      return acc;
    }, {});
  };

  // 5.4) Agrupamos filteredBills por categoría global y local
  const globalCats = groupBy(filteredBills, 'global_bill_category_id');
  const localCats = groupBy(filteredBills, 'local_bill_category_id');

  // 5.5) Creación de tonos de naranja para colores de serie
  function generateOrangeShades(baseHex: string, steps: number): string[] {
    const shades: string[] = [];
    for (let i = 0; i < steps; i++) {
      const r = Math.max(0, parseInt(baseHex.slice(1, 3), 16) - i * 10);
      const g = Math.max(0, parseInt(baseHex.slice(3, 5), 16) - i * 5);
      const b = Math.max(0, parseInt(baseHex.slice(5, 7), 16) - i * 2);
      shades.push(
        `#${r.toString(16).padStart(2, '0')}${g
          .toString(16)
          .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
      );
    }
    return shades;
  }
  const totalCatsCount = Object.keys(globalCats).length + Object.keys(localCats).length;
  const orangeShades = generateOrangeShades('#FC7556', totalCatsCount);

  // 5.6) Para cada categoría, creamos un arreglo de montos diarios alineado a `dailyLabels`.
  //      Si en un día no hay factura de esa categoría, se deja `null`.
  function createDailySeries(
    bills: BillType[],
    nameKey: 'global_bill_category_name' | 'local_bill_category_name'
  ): { data: (number | null)[]; label: string } {
    // Agrupamos las facturas de esa categoría por fecha ISO "YYYY-MM-DD"
    const perDayMap: Record<string, number> = {};
    bills.forEach((b) => {
      const iso = new Date(b.bill_date).toISOString().split('T')[0];
      if (!perDayMap[iso]) perDayMap[iso] = 0;
      perDayMap[iso] += b.amount;
    });

    // Construimos el arreglo de longitud dailyLabels
    const dataArray: (number | null)[] = dailyLabels.map((isoDate) => {
      return perDayMap[isoDate] != null ? perDayMap[isoDate] : null;
    });

    const categoryName = bills[0]?.[nameKey] || 'Desconocido';
    return { data: dataArray, label: categoryName };
  }

  // 5.7) Armamos los objetos `dailySeries` (global + local), asignando color dinámico
  let shadeIndex = 0;
  const dailyGlobalSeries = Object.entries(globalCats).map(([cid, bills]) => {
    const serie = createDailySeries(bills, 'global_bill_category_name');
    const color = orangeShades[shadeIndex++];
    return { ...serie, color };
  });
  const dailyLocalSeries = Object.entries(localCats).map(([cid, bills]) => {
    const serie = createDailySeries(bills, 'local_bill_category_name');
    const color = orangeShades[shadeIndex++];
    return { ...serie, color };
  });
  const dailySeries = [...dailyGlobalSeries, ...dailyLocalSeries];

  // ----------------------------
  // 6) Construir datos MENSUALES
  // ----------------------------
  // 6.1) Generamos un arreglo {month, year} desde actualStart hasta actualEnd
  const monthYearRange = getMonthYearRange(
    actualStart.getFullYear(),
    actualStart.getMonth() + 1,
    actualEnd.getFullYear(),
    actualEnd.getMonth() + 1
  );

  // 6.2) Creación de etiquetas mensuales tipo "Mayo 2025", "Jun 2025", ...
  const monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  const monthlyLabels = monthYearRange.map(({ month, year }) => `${monthNames[month - 1]} ${year}`);

  // 6.3) Función para crear serie mensual: suma de montos de esa categoría por mes-año
  function createMonthlySeries(
    bills: BillType[],
    nameKey: 'global_bill_category_name' | 'local_bill_category_name'
  ): { data: (number | null)[]; label: string } {
    // Agrupar las facturas de esa categoría por "YYYY-MM"
    const perMonthMap: Record<string, number> = {};
    bills.forEach((b) => {
      const d = new Date(b.bill_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; // "2025-05"
      if (!perMonthMap[key]) perMonthMap[key] = 0;
      perMonthMap[key] += b.amount;
    });

    // Construir el arreglo de longitud monthlyLabels
    const dataArray: (number | null)[] = monthYearRange.map(({ month, year }) => {
      const key = `${year}-${String(month).padStart(2, '0')}`; // "2025-05"
      return perMonthMap[key] != null ? perMonthMap[key] : null;
    });

    const categoryName = bills[0]?.[nameKey] || 'Desconocido';
    return { data: dataArray, label: categoryName };
  }

  // 6.4) Armamos `monthlySeries` (global + local), reusando orangeShades por índice
  shadeIndex = 0;
  const monthlyGlobalSeries = Object.entries(globalCats).map(([cid, bills]) => {
    const serie = createMonthlySeries(bills, 'global_bill_category_name');
    const color = orangeShades[shadeIndex++];
    return { ...serie, color };
  });
  const monthlyLocalSeries = Object.entries(localCats).map(([cid, bills]) => {
    const serie = createMonthlySeries(bills, 'local_bill_category_name');
    const color = orangeShades[shadeIndex++];
    return { ...serie, color };
  });
  const monthlySeries = [...monthlyGlobalSeries, ...monthlyLocalSeries];

  // ----------------------------
  // 7) Cálculos para CardSection (totales y variación)
  // ----------------------------
  const now = new Date();
  const thisM = now.getMonth() + 1;
  const thisY = now.getFullYear();
  const totalThis = billsList
    .filter((b) => {
      const d = new Date(b.bill_date);
      return d.getMonth() + 1 === thisM && d.getFullYear() === thisY;
    })
    .reduce((s, b) => s + b.amount, 0);

  let prevM = thisM - 1,
    prevY = thisY;
  if (prevM < 1) {
    prevM = 12;
    prevY--;
  }
  const totalPrev = billsList
    .filter((b) => {
      const d = new Date(b.bill_date);
      return d.getMonth() + 1 === prevM && d.getFullYear() === prevY;
    })
    .reduce((s, b) => s + b.amount, 0);

  const daysInPrevMonth = new Date(prevY, prevM, 0).getDate();
  const avgPrev = daysInPrevMonth > 0 ? totalPrev / daysInPrevMonth : 0;
  const acumPrevHastaHoy = avgPrev * now.getDate();
  const variation = acumPrevHastaHoy === 0 ? 0 : ((totalThis - acumPrevHastaHoy) / acumPrevHastaHoy) * 100;
  const isNeg = variation < 0;
  const arrowColor = isNeg ? 'green' : 'red';

  const billsCurr = billsList.filter((b) => {
    const d = new Date(b.bill_date);
    return d.getMonth() + 1 === thisM && d.getFullYear() === thisY;
  });
  const maxBill = billsCurr.length
    ? billsCurr.reduce((p, c) => (p.amount > c.amount ? p : c))
    : undefined;

  // ----------------------------
  // 8) Renderizado final
  // ----------------------------
  return (
    <div style={{ overflowX: 'hidden' }}>
      {/* Tarjeta superior con totales y variación */}
      <CardSection
        totalCurrentMonth={totalThis}
        variationPercent={variation}
        arrowColor={arrowColor}
        isNegative={isNeg}
        maxBillThisMonth={maxBill}
      />

      {/* Gráfico con datos diarios y mensuales */}
      <ChartSection
        dailyLabels={dailyLabels}
        dailySeries={dailySeries}
        monthlyLabels={monthlyLabels}
        monthlySeries={monthlySeries}
      />
    </div>
  );
};

export default Estadistica;
